import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  MailX, Search, Download, Loader2, Undo2, ChevronRight, ShieldCheck, Check,
} from 'lucide-react';
import api from '../lib/api';
import { PageHeader } from '../components/layout/PageHeader';
import { EmptyState } from '../components/layout/EmptyState';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Skeleton } from '../components/ui/skeleton';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { downloadCsv } from '../lib/csv';

const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return '—'; }
};

export default function Unsubscribed() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState(null); // 'bulk' | contact object

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/contacts', { params: { status: 'unsubscribed' } });
      setContacts(data || []);
    } catch (e) { toast.error('Could not load the unsubscribe list'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { const t = setTimeout(load, 0); return () => clearTimeout(t); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) =>
      (c.email || '').toLowerCase().includes(q)
      || `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase().includes(q));
  }, [contacts, search]);

  const allSelected = filtered.length > 0 && filtered.every((c) => selected.has(c.id));
  const toggleAll = () => setSelected((prev) => {
    const all = filtered.length > 0 && filtered.every((c) => prev.has(c.id));
    const n = new Set(prev);
    filtered.forEach((c) => (all ? n.delete(c.id) : n.add(c.id)));
    return n;
  });
  const toggleOne = (id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const clearSelection = () => setSelected(new Set());

  const doRestore = async (ids) => {
    if (ids.length === 0) return;
    setBusy(true);
    try {
      await api.post('/contacts/bulk-resubscribe', { ids });
      setContacts((prev) => prev.filter((c) => !ids.includes(c.id)));
      clearSelection();
      toast.success(ids.length === 1 ? 'Contact restored to your active list' : `${ids.length} contacts restored`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not restore');
    } finally { setBusy(false); setConfirmRestore(null); }
  };

  const exportCsv = () => {
    if (filtered.length === 0) { toast.error('Nothing to export'); return; }
    const headers = [
      { key: 'first_name', label: 'First name' }, { key: 'last_name', label: 'Last name' },
      { key: 'email', label: 'Email' }, { key: 'phone', label: 'Phone' },
      { key: 'company', label: 'Company' }, { key: 'unsubscribed_at', label: 'Unsubscribed' },
    ];
    const rows = filtered.map((c) => ({
      first_name: c.first_name || '', last_name: c.last_name || '',
      email: c.email || '', phone: c.phone || '', company: c.company || '',
      unsubscribed_at: fmtDate(c.unsubscribed_at),
    }));
    downloadCsv('thrive-unsubscribed', headers, rows);
    toast.success(`Exported ${filtered.length} contacts`);
  };

  const restoreTargetIds = confirmRestore === 'bulk'
    ? [...selected]
    : confirmRestore ? [confirmRestore.id] : [];

  return (
    <div data-testid="unsubscribed-page">
      <PageHeader
        title="Unsubscribed"
        subtitle="People who opted out of your emails. They're kept separate from your contacts and are never emailed — restore anyone to add them back to your active list."
      >
        <Button variant="secondary" onClick={exportCsv} className="gap-2" data-testid="unsubscribed-export-button">
          <Download className="h-4 w-4" /> Export
        </Button>
      </PageHeader>

      <div className="flex items-center gap-3 mb-4 p-4 rounded-xl bg-secondary/50 border border-border" data-testid="unsubscribed-info">
        <ShieldCheck className="h-5 w-5 text-primary flex-shrink-0" />
        <p className="text-sm text-muted-foreground">
          These contacts are suppressed from all event emails to honor their opt-out. Their details are kept private and safe in case you need them.
        </p>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or email" className="pl-9 h-11" data-testid="unsubscribed-search" />
      </div>

      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4 rounded-xl border border-primary/20 bg-primary/5 px-3 py-2" data-testid="unsubscribed-bulk-bar">
          <span className="text-sm font-medium text-primary px-1">{selected.size} selected</span>
          <Button size="sm" className="gap-1.5 h-9" disabled={busy} onClick={() => setConfirmRestore('bulk')} data-testid="bulk-restore-button">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />} Restore to contacts
          </Button>
          <Button size="sm" variant="ghost" className="h-9" onClick={clearSelection} data-testid="bulk-clear-button">Clear</Button>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-border bg-card">
          <EmptyState
            icon={contacts.length === 0 ? MailX : Search}
            title={contacts.length === 0 ? 'No one has unsubscribed' : 'No matches'}
            description={contacts.length === 0
              ? 'When a guest opts out of your emails — or you unsubscribe a contact — they will appear here, separate from your active contacts.'
              : 'Try a different search.'}
          />
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="unsubscribed-list">
          <div className="flex items-center gap-3 px-4 py-2.5 border-b border-border bg-secondary/30">
            <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="unsubscribed-select-all" />
            <span className="text-xs text-muted-foreground">Select all ({filtered.length})</span>
          </div>
          {filtered.map((c) => (
            <div key={c.id} className={`flex items-center gap-3 px-4 py-3 border-b border-border last:border-0 transition-colors ${selected.has(c.id) ? 'bg-primary/5' : ''}`} data-testid="unsubscribed-row">
              <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggleOne(c.id)} data-testid="unsubscribed-checkbox" />
              <div className="h-10 w-10 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
                <MailX className="h-4.5 w-4.5 text-accent" />
              </div>
              <div className="min-w-0 flex-1">
                <button
                  onClick={() => navigate(`/app/contacts/${c.id}`)}
                  className="font-medium text-foreground truncate hover:text-primary transition-colors text-left inline-flex items-center gap-1 group"
                  data-testid="unsubscribed-name-link"
                >
                  {`${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email}
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
                <div className="text-sm text-muted-foreground truncate">{c.email}</div>
              </div>
              <div className="hidden sm:block text-sm text-muted-foreground w-32 text-right">
                {c.unsubscribed_at ? `Opted out ${fmtDate(c.unsubscribed_at)}` : ''}
              </div>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => setConfirmRestore(c)} className="gap-1.5" data-testid="unsubscribed-restore">
                <Undo2 className="h-4 w-4" /><span className="hidden sm:inline">Restore</span>
              </Button>
            </div>
          ))}
        </div>
      )}
      {!loading && filtered.length > 0 && (
        <p className="text-sm text-muted-foreground mt-3">{filtered.length} unsubscribed contact{filtered.length !== 1 ? 's' : ''}</p>
      )}

      <AlertDialog open={!!confirmRestore} onOpenChange={(o) => !o && setConfirmRestore(null)}>
        <AlertDialogContent data-testid="unsubscribed-restore-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Restore {restoreTargetIds.length > 1 ? `${restoreTargetIds.length} contacts` : 'this contact'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This moves {restoreTargetIds.length > 1 ? 'them' : 'them'} back to your active contacts and allows them to receive event emails again. Only do this if they&apos;ve agreed to be re-added.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="restore-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => doRestore(restoreTargetIds)} className="gap-1.5" data-testid="restore-confirm">
              <Check className="h-4 w-4" /> Restore
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
